import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Users, Clock, MapPin } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import { CALENDLY_URL, PROGRAMMES } from "@/data/content";

const ICONS = {
  "expert-to-leader": Users,
  "leadership-coaching": Clock,
  "atlantis": MapPin,
};

export default function Programmes() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    }
  }, [hash]);

  return (
    <div data-testid="programmes-page">
      {/* Hero */}
      <section className="pt-36 lg:pt-44 pb-16 bg-gwa-bg">
        <div className="gwa-container grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-2">
            <Reveal><p className="gwa-eyebrow"><span className="gwa-rule" />Programmes</p></Reveal>
          </div>
          <div className="lg:col-span-10">
            <Reveal delay={120}>
              <h1 className="gwa-display text-5xl lg:text-7xl text-gwa-primary leading-[1.02]">
                Three engagements,<br />designed for <em className="italic font-light text-gwa-purple">senior reality.</em>
              </h1>
            </Reveal>
            <Reveal delay={220}>
              <p className="mt-10 text-lg text-gwa-muted-fg max-w-3xl leading-relaxed">
                Each programme is designed for organisations that want measurable shift in leadership capability. Outcomes, format, and fit are spelled out below, so you can scope an internal conversation quickly.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Programme nav */}
      <section className="border-y border-gwa-border bg-gwa-ivory sticky top-20 z-30 backdrop-blur-md bg-gwa-ivory/90">
        <div className="gwa-container py-5 flex flex-wrap gap-x-10 gap-y-3 justify-center">
          {PROGRAMMES.map((p) => (
            <a
              key={p.slug}
              href={`#${p.slug}`}
              data-testid={`prog-nav-${p.slug}`}
              className="gwa-link-underline text-xs uppercase tracking-[0.18em] font-semibold text-gwa-primary"
            >
              {p.title}
            </a>
          ))}
        </div>
      </section>

      {/* Programmes detailed */}
      {PROGRAMMES.map((p, idx) => {
        const Icon = ICONS[p.slug] || Users;
        const dark = idx === 0;
        return (
          <section
            key={p.slug}
            id={p.slug}
            data-testid={`programme-section-${p.slug}`}
            className={`scroll-mt-32 ${dark ? "bg-[#651A75] text-gwa-bg" : "bg-gwa-bg"} ${dark ? "gwa-noise" : ""}`}
          >
            <div className="gwa-container py-24 lg:py-32 relative z-10">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                <div className={`lg:col-span-5 ${p.slug === "atlantis" ? "order-2 lg:pl-12" : ""}`}>
                  <Reveal>
                    <p className={dark ? "gwa-eyebrow-light" : "gwa-eyebrow"}>
                      <span className="gwa-rule" />{p.eyebrow}
                    </p>
                    <h2 className={`gwa-display text-5xl lg:text-6xl mt-6 leading-[1.02] ${dark ? "" : "text-gwa-primary"}`}>
                      {p.title}
                    </h2>
                    {p.subtitle && (
                      <p className={`font-serif italic text-xl lg:text-2xl mt-4 leading-snug ${dark ? "text-gwa-gold" : "text-gwa-purple"}`}>
                        {p.subtitle}
                      </p>
                    )}
                    <p className={`mt-7 text-lg leading-relaxed ${dark ? "text-gwa-bg/85" : "text-gwa-muted-fg"}`}>
                      {p.lede}
                    </p>
                    {p.midItalic && (
                      <p className={`mt-5 font-serif italic text-lg lg:text-xl leading-snug ${dark ? "text-gwa-gold" : "text-gwa-purple"}`}>
                        {p.midItalic}
                      </p>
                    )}
                    {p.headlineOutcome && (
                      <p className={`mt-5 text-lg font-medium leading-snug ${dark ? "text-gwa-bg" : "text-gwa-primary"}`}>
                        {p.headlineOutcome}
                      </p>
                    )}
                    {p.bodyParagraphs && (
                      <div className={`mt-7 space-y-4 text-base lg:text-[1.05rem] leading-relaxed ${dark ? "text-gwa-bg/80" : "text-gwa-muted-fg"}`}>
                        {p.bodyParagraphs.map((para, i) => (
                          <p key={i}>{para}</p>
                        ))}
                      </div>
                    )}
                    {p.emphasis && (
                      <div className={`mt-8 pt-6 border-t space-y-1.5 ${dark ? "border-white/15" : "border-gwa-border"}`}>
                        {p.emphasis.map((line, i) => (
                          <p key={i} className={`font-serif text-xl lg:text-[1.35rem] leading-snug font-medium ${dark ? "text-gwa-gold" : "text-gwa-purple"}`}>
                            {line}
                          </p>
                        ))}
                      </div>
                    )}
                    {p.practitionerNote && (
                      <div className={`mt-8 p-6 lg:p-7 ${dark ? "bg-white/5 border border-white/10" : "bg-gwa-ivory border border-gwa-border"}`}>
                        <p className={`text-[0.7rem] tracking-[0.22em] uppercase font-semibold mb-3 ${dark ? "text-gwa-gold" : "text-gwa-purple"}`}>
                          Practitioner-led
                        </p>
                        <p className={`font-serif italic text-lg lg:text-xl leading-snug ${dark ? "text-gwa-bg/95" : "text-gwa-primary"}`}>
                          {p.practitionerNote}
                        </p>
                      </div>
                    )}
                    <div className={`mt-10 flex items-start gap-3 pt-6 border-t ${dark ? "border-white/10" : "border-gwa-border"}`}>
                      <Icon className={`w-5 h-5 mt-0.5 ${dark ? "text-gwa-accent" : "text-gwa-accent"}`} />
                      <p className={`text-sm uppercase tracking-[0.16em] ${dark ? "text-gwa-bg/80" : "text-gwa-primary/80"}`}>
                        {p.format}
                      </p>
                    </div>
                  </Reveal>
                </div>

                <div className={`lg:col-span-7 ${p.slug === "atlantis" ? "order-1 lg:pl-0" : "lg:pl-12"}`}>
                  <Reveal delay={120}>
                    <p className={dark ? "gwa-eyebrow-light" : "gwa-eyebrow"}>What participants leave with</p>
                    <ul className={`mt-6 space-y-5 ${dark ? "border-l border-white/15" : "border-l border-gwa-border"}`}>
                      {p.outcomes.map((o, i) => (
                        <li key={i} className="pl-6 relative">
                          <span className="absolute left-0 top-3 -translate-x-1/2 w-2 h-2 bg-gwa-accent" />
                          <p className={`font-serif text-xl lg:text-[1.4rem] leading-snug ${dark ? "" : "text-gwa-primary"}`}>
                            {o}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </Reveal>

                  {p.organisationsGain && (
                    <Reveal delay={170}>
                      <div className="mt-12">
                        <p className={dark ? "gwa-eyebrow-light" : "gwa-eyebrow"}>What organisations gain</p>
                        <ul className={`mt-6 space-y-5 ${dark ? "border-l border-white/15" : "border-l border-gwa-border"}`}>
                          {p.organisationsGain.map((o, i) => (
                            <li key={i} className="pl-6 relative">
                              <span className="absolute left-0 top-3 -translate-x-1/2 w-2 h-2 bg-gwa-accent" />
                              <p className={`font-serif text-xl lg:text-[1.4rem] leading-snug ${dark ? "" : "text-gwa-primary"}`}>
                                {o}
                              </p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </Reveal>
                  )}

                  <Reveal delay={200}>
                    <div className={`mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6 ${dark ? "" : ""}`}>
                      <div>
                        <p className={dark ? "gwa-eyebrow-light" : "gwa-eyebrow"}>Structure</p>
                        <ul className={`mt-5 space-y-3 ${dark ? "text-gwa-bg/80" : "text-gwa-muted-fg"}`}>
                          {p.sessions.map((s, i) => (
                            <li key={i} className="flex items-start gap-3 leading-relaxed">
                              <span className="text-gwa-accent leading-tight">•</span>
                              <span>{s}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className={dark ? "gwa-eyebrow-light" : "gwa-eyebrow"}>Best fit</p>
                        <p className={`mt-5 leading-relaxed ${dark ? "text-gwa-bg/80" : "text-gwa-muted-fg"}`}>{p.fit}</p>
                      </div>
                    </div>
                  </Reveal>

                  <Reveal delay={280}>
                    <div className="mt-12 flex flex-col sm:flex-row gap-4">
                      <Link
                        to="/contact"
                        data-testid={`prog-cta-pipeline-${p.slug}`}
                        className={dark ? "gwa-btn-primary bg-gwa-bg text-gwa-primary border-gwa-bg hover:bg-gwa-gold hover:border-gwa-gold" : "gwa-btn-primary"}
                      >
                        Discuss your leadership pipeline <ArrowUpRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </Reveal>
                </div>
              </div>

              {(p.postSession || p.businessCase) && (
                <div className={`mt-16 lg:mt-24 pt-12 lg:pt-16 border-t ${dark ? "border-white/15" : "border-gwa-border"}`}>
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    {p.postSession && (
                      <div className="lg:col-span-7">
                        <Reveal>
                          <p className={dark ? "gwa-eyebrow-light" : "gwa-eyebrow"}><span className="gwa-rule" />After the day</p>
                          <h3 className={`gwa-display text-3xl lg:text-[2.5rem] mt-6 leading-[1.1] ${dark ? "" : "text-gwa-primary"}`}>
                            {p.postSession.heading}
                          </h3>
                          <div className={`mt-7 space-y-5 text-lg leading-[1.7] font-light ${dark ? "text-gwa-bg/85" : "text-gwa-muted-fg"}`}>
                            {p.postSession.paragraphs.map((para, i) => (
                              <p key={i}>{para}</p>
                            ))}
                          </div>
                        </Reveal>
                      </div>
                    )}
                    {p.businessCase && (
                      <div className="lg:col-span-5">
                        <Reveal delay={120}>
                          <div className={`p-7 lg:p-9 ${dark ? "bg-white/5 border border-white/10" : "bg-gwa-ivory border border-gwa-border"}`}>
                            <p className={`text-[0.7rem] tracking-[0.22em] uppercase font-semibold mb-4 ${dark ? "text-gwa-gold" : "text-gwa-purple"}`}>
                              The business case
                            </p>
                            <p className={`font-serif text-xl lg:text-[1.4rem] leading-snug ${dark ? "text-gwa-bg/95" : "text-gwa-primary"}`}>
                              {p.businessCase}
                            </p>
                          </div>
                        </Reveal>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </section>
        );
      })}

      {/* Closing */}
      <section className="bg-gwa-secondary">
        <div className="gwa-container py-24 lg:py-32 text-center">
          <Reveal>
            <p className="gwa-eyebrow"><span className="gwa-rule" />Mix and match</p>
            <h2 className="gwa-display text-4xl lg:text-6xl mt-7 text-gwa-primary max-w-4xl mx-auto leading-[1.05]">
              Most engagements combine more than one of these formats.
            </h2>
            <p className="mt-8 text-gwa-muted-fg max-w-2xl mx-auto leading-relaxed">
              Common combinations: an Atlantis simulation as entry point, followed by a cohort programme; or a cohort programme with individual coaching for those identified as next in line for senior leadership.
            </p>
            <div className="mt-12">
              <Link to="/contact" data-testid="prog-closing-pipeline" className="gwa-btn-primary">
                Discuss your leadership pipeline <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
